import type { CSSProperties, MouseEvent } from 'react';
import React from 'react';
import { Button } from '@noths/polaris-design-system-core-components';

import * as styles from './HeroCarouselCaption.styles';

export interface HeroCarouselCaptionProps {
  buttonHref: string;
  buttonText: string;
  fullWidth?: boolean;
  onButtonClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  onTermsClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
  paragraph: string;
  /**
   * Reserve the height of one terms line even when this slide has none — set when any slide in the
   * carousel has T&Cs, so switching slides doesn't change the carousel's height.
   */
  reserveTermsSpace?: boolean;
  termsAndConditions?: string;
  /**
   * Turns the T&Cs text into a link to the campaign page. Ignored without `termsAndConditions`,
   * since there would be no text to link.
   */
  termsAndConditionsHref?: string;
}

/**
 * The non-sliding part of a hero slide: paragraph, optional T&Cs and the CTA. `HeroCarousel`
 * renders a single caption for the active slide and re-mounts it (via `key`) on each slide change.
 * Only the text fades in (see `HeroCarouselCaption.styles.ts`); the CTA and layout positions stay
 * steady — nothing slides or moves.
 */
export const HeroCarouselCaption = ({
  buttonHref,
  buttonText,
  fullWidth,
  onButtonClick,
  onTermsClick,
  paragraph,
  reserveTermsSpace,
  termsAndConditions,
  termsAndConditionsHref,
}: HeroCarouselCaptionProps) => (
  <div css={styles.caption} style={{ '--width': fullWidth ? '100%' : '86%' } as CSSProperties}>
    <div css={styles.textBlock}>
      <p css={styles.paragraph}>{paragraph}</p>
      {termsAndConditions ? (
        <p css={styles.terms}>
          {termsAndConditionsHref ? (
            <a css={styles.termsLink} href={termsAndConditionsHref} onClick={onTermsClick}>
              {termsAndConditions}
            </a>
          ) : (
            termsAndConditions
          )}
        </p>
      ) : (
        reserveTermsSpace && (
          <p
            aria-hidden="true"
            css={[styles.terms, styles.termsPlaceholder]}
            data-testid="terms-placeholder"
          >
            {' '}
          </p>
        )
      )}
    </div>
    <div css={styles.cta}>
      <Button href={buttonHref} onClick={onButtonClick}>
        {buttonText}
      </Button>
    </div>
  </div>
);
